import { Duration } from "luxon";
import * as React from "react";
import { FC, useMemo } from "react";
import { SizeClass } from "shared-ts-ui-responsive";
import { SxProps, Typography } from "@mui/material";
import { DurationFormatter } from "./DurationFormatter";
import { useLocaleString } from "shared-ts-locale";
import { IllegalArgumentError } from "shared-ts-utils-core";

type LocalesType =
  | Intl.UnicodeBCP47LocaleIdentifier
  | Array<Intl.UnicodeBCP47LocaleIdentifier>;

export interface DurationDisplayProps {
  locales?: LocalesType;
  duration: Duration<true>;
  timeDirection?: "past" | "future" | "none";
  suffix?: string;
  maxUnits: number;
  shiftTo?: Intl.RelativeTimeFormatUnit[];
  layout?: SizeClass;
  timeFormatStyle?: Intl.RelativeTimeFormatStyle;
  component?: "p" | "span";
  sx?: SxProps;
}

export const DurationDisplay: FC<DurationDisplayProps> = (props) => {
  const layout = props.layout ?? SizeClass.Medium;
  const localeString = useLocaleString();

  if (!props.duration.isValid) {
    throw new IllegalArgumentError("duration is invalid");
  }
  const locales = props.locales != null ? props.locales : localeString;

  const parts = useMemo(() => {
    const duration =
      props.shiftTo !== undefined
        ? props.duration.shiftTo(...props.shiftTo)
        : props.duration.shiftToAll();

    const lastUnit =
      props.shiftTo !== undefined
        ? props.shiftTo[props.shiftTo.length - 1]
        : undefined;

    return new DurationFormatter({
      locales: locales,
      options: {
        maxNumberOfParts: props.maxUnits,
        roundValues: true,
        timeDirection: props.timeDirection,
        lastUnit: lastUnit,
        style:
          props.timeFormatStyle != null
            ? props.timeFormatStyle
            : layout === SizeClass.Compact
            ? "short"
            : "long",
      },
    }).formatToParts(duration);
  }, [
    props.shiftTo,
    props.duration,
    props.maxUnits,
    props.timeDirection,
    props.timeFormatStyle,
    locales,
    layout,
  ]);

  return (
    <Typography
      className={"DurationDisplay"}
      sx={props.sx}
      component={props.component === "span" ? "span" : "div"}
    >
      {parts.map((part, index) => {
        const isFirst = index === 0;
        const isLast = index === parts.length - 1;

        return (
          <span style={{ whiteSpace: "nowrap" }} key={index}>
            {`${part.value}${
              part.type === "literal" && !(isLast || isFirst) ? " " : ""
            }`}
          </span>
        );
      })}
      {props.suffix != null && ` ${props.suffix}`}
    </Typography>
  );
};
